import { RefreshCw } from "lucide-react";
import { useState } from "react";
import { useToast } from "../../contexts/ToastContext";
import { useTiltScout } from "../../hooks/useTiltScout";
import type { ReactNode } from "react";
import { normalizeTiltScoutReadLabel } from "./tilt-scout-copy";
import { MarketPriceActionReference } from "../market/MarketPriceActionReference";
import { TiltScoutInvalidationDrivers } from "./TiltScoutInvalidationDrivers";
import { TiltScoutRelatedNarratives } from "./TiltScoutRelatedNarratives";
import type { TiltScoutSnapshot } from "./tilt-scout-types";
import { FintheonNumericText } from "../numbers/FintheonNumericText";

interface TiltScoutStripProps {
  selectedSymbol: string;
}

interface TiltScoutPanelProps {
  snapshot: TiltScoutSnapshot;
  refresh: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
  className?: string;
}

export function TiltScoutStrip({ selectedSymbol }: TiltScoutStripProps) {
  const { snapshot, refresh, isLoading, error } = useTiltScout(selectedSymbol);

  return (
    <TiltScoutPanel
      snapshot={snapshot}
      refresh={refresh}
      isLoading={isLoading}
      error={error}
    />
  );
}

export function TiltScoutPanel({
  snapshot,
  refresh,
  className = "",
}: TiltScoutPanelProps) {
  const { addToast } = useToast();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const readLabel = normalizeTiltScoutReadLabel(snapshot.label);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refresh();
      addToast("TiltScout refreshed", "success", readLabel);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <section
      data-testid="tiltscout-panel"
      className={`rounded-md border border-[color-mix(in_srgb,var(--fintheon-accent)_14%,transparent)] bg-[var(--fintheon-surface)] px-3 py-2 ${className}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold text-[var(--fintheon-accent)]">
              TiltScout
            </span>
            <span className="font-mono text-[10px] text-[var(--fintheon-muted)]">
              {snapshot.canonicalSymbol}
            </span>
          </div>
          <p className="mt-1 whitespace-normal break-words text-[11px] leading-snug text-[var(--fintheon-text)]/82">
            {snapshot.eventText}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="grid h-7 w-7 place-items-center rounded-[4px] text-[var(--fintheon-muted)] transition hover:text-[var(--fintheon-accent)] disabled:opacity-40 focus-visible:outline-none"
            title="Refresh TiltScout"
            aria-label="Refresh TiltScout"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${isRefreshing ? "animate-spin" : ""}`}
              aria-hidden
            />
          </button>
        </div>
      </div>

      <div className="mt-2 grid grid-cols-[minmax(0,1fr)_128px] items-end gap-x-3 gap-y-1.5">
        <div className="col-span-2 flex items-center justify-between gap-3">
          <span className="text-[9px] uppercase tracking-[0.13em] text-[var(--fintheon-muted)]">
            Read
          </span>
          <span
            className="text-right text-[10px] font-semibold lowercase"
            style={{ color: scoreColor(snapshot.posture) }}
          >
            {snapshot.posture}
          </span>
        </div>
        <div className="col-start-2 w-[128px] justify-self-end text-right">
          <span
            className="block font-mono text-lg leading-none tabular-nums"
            style={{ color: scoreColor(snapshot.posture) }}
          >
            <FintheonNumericText
              kind="score"
              text={formatScore(snapshot.score)}
            />
          </span>
          <div className="mt-1">
            <TiltMeter score={snapshot.score} posture={snapshot.posture} />
          </div>
        </div>
      </div>

      <div className="mt-2 grid gap-1.5">
        {snapshot.axisScores.map((axis) => (
          <AxisFuse key={axis.id} axis={axis} />
        ))}
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2">
        <MarketPriceActionReference
          symbol={snapshot.selectedSymbol}
          label="Prices of Interest"
          variant="tiltscout"
          className="col-span-2"
          initialDossier={snapshot.marketPerformance?.marketAwareness ?? null}
        />
        <MiniFact
          label="Window"
          value={snapshot.deskPlanPreview?.windowText ?? snapshot.eventText}
        />
        <TiltScoutInvalidationDrivers
          snapshot={snapshot}
          className="col-span-2"
        />
        <MiniFact
          label="Next Event"
          value={snapshot.deskPlanPreview?.eventText ?? snapshot.eventText}
          multiline
        />
        <TiltScoutRelatedNarratives
          catalysts={snapshot.recentRelatedCatalysts}
          className="col-span-2"
        />
      </div>
    </section>
  );
}

function MiniFact({
  icon,
  label,
  value,
  className = "",
  multiline = false,
}: {
  icon?: ReactNode;
  label: string;
  value: string;
  className?: string;
  multiline?: boolean;
}) {
  return (
    <div
      className={`min-w-0 rounded-[4px] bg-[var(--fintheon-bg)]/22 px-2 py-1.5 ${className}`}
    >
      <div className="flex items-center gap-1 text-[9px] text-[var(--fintheon-muted)]">
        {icon}
        <span>{label}</span>
      </div>
      <p
        className={`mt-0.5 text-[10px] leading-snug text-[var(--fintheon-text)]/76 ${
          multiline ? "whitespace-normal break-words" : "truncate"
        }`}
      >
        <FintheonNumericText text={value} />
      </p>
    </div>
  );
}

function TiltMeter({
  score,
  posture,
}: {
  score: number;
  posture: TiltScoutSnapshot["posture"];
}) {
  const offset = Math.min(50, Math.abs(score) * 5);
  const left = score < 0 ? `${50 - offset}%` : "50%";
  return (
    <div className="relative h-1 rounded-full bg-[var(--fintheon-bg)]/60">
      <div
        className="absolute top-0 h-full rounded-full"
        style={{
          left,
          width: `${offset}%`,
          backgroundColor: scoreColor(posture),
        }}
      />
      <div className="absolute left-1/2 top-[-3px] h-[10px] w-px bg-[var(--fintheon-muted)]/30" />
    </div>
  );
}

function AxisFuse({ axis }: { axis: TiltScoutSnapshot["axisScores"][number] }) {
  return (
    <div className="grid grid-cols-[78px_1fr_36px] items-center gap-2">
      <span className="text-[9px] uppercase tracking-[0.13em] text-[var(--fintheon-muted)]">
        {axis.label}
      </span>
      <TiltMeter score={axis.score} posture={axis.posture} />
      <span
        className="text-right font-mono text-[10px]"
        style={{ color: scoreColor(axis.posture) }}
      >
        <FintheonNumericText kind="score" text={formatScore(axis.score)} />
      </span>
    </div>
  );
}

function formatScore(score: number) {
  return `${score > 0 ? "+" : ""}${score.toFixed(1)}`;
}

function scoreColor(posture: TiltScoutSnapshot["posture"]) {
  if (posture === "bullish") return "var(--fintheon-bullish)";
  if (posture === "bearish") return "var(--fintheon-bearish)";
  return "var(--fintheon-tilt)";
}
